import React, {
  FunctionComponent,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef
} from 'react'
import EmailInput from './emailInput'
import ChoosePackage from './choosePackage'
import ChooseNumber from './chooseNumber'
import classNames from 'classnames'
import { Button, TextField } from '@mui/material'
import useIntl from '@/utils/intl'
import ChooseSim from './chooseSim'
import ContactInfo from './contactInfo'
import VerificationSection from './verification'
import { Obj } from '..'
import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import { useMobiApi } from '@/services/mobi-api'
import { useToast } from '@/context/ToastContext'
import { resolveImageUrl } from '@/utils/constants'
import Image from 'next/image'
import { ChoosenInfo, serviceDetail } from '@/services/mobi-api/numberv2/types'
import { moneyFormatter } from '@/utils/helpers'
import { useTheme } from '@/context/ThemeContext'
import { useLanguage } from '@/context/LanguageContext'
import { trackNewNumberEvent } from '@/utils/newNumberFlowTracking'
import { useAuth } from '@/context/AuthContext'

interface Props {
  onClick: (objs: Obj) => void
}

interface State {
  checkBtnDisabled?: boolean
  inputRefValue?: string
  isRefCodeValid?: boolean
}

const blurActiveEditableElement = (target?: EventTarget | null): void => {
  if (typeof document === 'undefined' || typeof Node === 'undefined') {
    return
  }

  const activeElement = document.activeElement
  if (!(activeElement instanceof HTMLElement)) {
    return
  }

  const tagName = activeElement.tagName.toLowerCase()
  const isEditableElement =
    tagName === 'input' ||
    tagName === 'textarea' ||
    activeElement.isContentEditable

  if (!isEditableElement) {
    return
  }

  if (target instanceof Node && activeElement.contains(target)) {
    return
  }

  activeElement.blur()
}

const Step1: FunctionComponent<Props> = ({ onClick }) => {
  const [currentStep, setCurrentStep] = React.useState(0)
  const { translate } = useIntl()
  const [objs, setObj] = React.useState<Obj | undefined>()
  const { numberv2 } = useMobiApi()
  const { getInstanceV2, getPhoneNumbersByRegno } = numberv2
  const { showError } = useToast()
  const { locale } = useLanguage()
  const [isExpanded, setIsExpanded] = React.useState(false)
  const [isContactValid, setContactValid] = React.useState(false)
  const [isContactSubmitting, setContactSubmitting] = React.useState(false)
  const [guardianRegno, setGuardianRegno] = React.useState('')
  const [suggestedPhoneNumber, setSuggestedPhoneNumber] = React.useState('')
  const [suggestedPhoneNumbers, setSuggestedPhoneNumbers] = React.useState<string[]>([])
  const contactSubmitHandlerRef = React.useRef<(() => void) | undefined>(undefined)
  const contactTouchSubmitAtRef = React.useRef(0)
  const starsSupportRequestKeyRef = useRef('')
  const starsSupportInstanceFetchRef = useRef('')
  const [liveSelectedServices, setLiveSelectedServices] =
    React.useState<ChoosenInfo>({})
  const bottomRef = useRef<HTMLDivElement>(null)
  const currentStepRef = useRef(0)
  const subStepStartedAtRef = useRef(Date.now())
  const previousSubStepIndexRef = useRef<number | null>(null)
  const isCompletedRef = useRef(false)
  const autoSkipHandledRef = useRef(false)
  const viewportInitialHeightRef = React.useRef<number | null>(null)
  const [bottomHeight, setBottomHeight] = React.useState(96)
  const [state, setState] = useReducer(
    (state: State, newState: State) => ({ ...state, ...newState }),
    {
      checkBtnDisabled: true,
      inputRefValue: '',
      isRefCodeValid: false
    }
  )
  const { theme } = useTheme()
  const { isWebView } = useAuth()
  // Query params are client-only; set them after mount to avoid SSR/CSR mismatch
  const [initialSelectedChildRegnoFromQuery, setInitialSelectedChildRegnoFromQuery] = React.useState<string | undefined>(undefined)
  const [initialContact1FromQuery, setInitialContact1FromQuery] = React.useState<{
    name?: string
    who?: string
    phone?: string
  } | undefined>(undefined)
  const [skipVerificationFromQuery, setSkipVerificationFromQuery] =
    React.useState(false)

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    const urlParams = new URLSearchParams(window.location.search)
    const childId = urlParams.get('childId') ?? undefined
    const skipVerificationParam = urlParams.get('skipVerification')
    const contact = {
      name: urlParams.get('contactName') ?? undefined,
      who: urlParams.get('contactWho') ?? undefined,
      phone: urlParams.get('contactPhone') ?? undefined
    }
    setInitialSelectedChildRegnoFromQuery(childId)
    setInitialContact1FromQuery(contact)
    setSkipVerificationFromQuery(
      skipVerificationParam === '1' || skipVerificationParam === 'true'
    )
    if (childId) {
      try {
        sessionStorage.setItem('mc_childId', childId)
      } catch (e) {
        // ignore
      }
    }
  }, [])

  
  const starsSignals = [
    objs?.rateplan,
    objs?.choosenInfo?.choosenPackage?.rateplan,
    objs?.choosenPackage,
    objs?.choosenInfo?.choosenPackage?.name
  ].filter((value): value is string => typeof value === 'string' && value !== '')
  const isPrepaidStars = starsSignals.some((value) => {
    const normalized = value.toLowerCase()
    return normalized.includes('prsta') || normalized.includes('stars')
  })
  const stepIds = React.useMemo(
    () =>
      isPrepaidStars
        ? ['email', 'package', 'number', 'sim', 'verification', 'contact']
        : ['email', 'package', 'number', 'sim', 'contact'],
    [isPrepaidStars]
  )
  const currentStepId = stepIds[currentStep] ?? ''
  const showBottomSummary = currentStepId !== 'verification'
  const shouldSkipVerification =
    isPrepaidStars &&
    (
      (initialSelectedChildRegnoFromQuery?.trim() ?? '') !== '' ||
      skipVerificationFromQuery
    )

  const scrollToSection = React.useCallback((sectionId: string): void => {
    if (typeof window !== 'undefined') {
      const currentViewportHeight = window.visualViewport?.height ?? window.innerHeight
      const initial = viewportInitialHeightRef.current ?? window.innerHeight
      if (currentViewportHeight < (initial - 150)) {
        return
      }
    }

    const performScroll = (): void => {
      const sectionElement = document.getElementById(sectionId)
      if (sectionElement != null) {
        sectionElement.scrollIntoView({
          behavior: isWebView ? 'auto' : 'smooth',
          block: 'start',
          inline: 'nearest'
        })
      }
    }

    if (typeof window !== 'undefined' && isWebView) {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          performScroll()
        })
      })
      return
    }

    performScroll()
  }, [isWebView])

  React.useEffect(() => {
    if (!shouldSkipVerification) return
    if (autoSkipHandledRef.current) return

    const currentStepInEffect = stepIds[currentStep]
    if (currentStepInEffect === 'verification') {
      autoSkipHandledRef.current = true
      const contactIndex = stepIds.indexOf('contact')
      if (contactIndex >= 0) {
        blurActiveEditableElement()
        setCurrentStep(contactIndex)
        scrollToSection('contact')
      }
    }
  }, [currentStep, scrollToSection, shouldSkipVerification, stepIds])

  useLayoutEffect(() => {
    const viewportInitialHeightRef = { current: typeof window !== 'undefined' ? window.innerHeight : 0 }

    const updateHeight = () => {
      if (bottomRef.current == null) return
      const currentViewportHeight = typeof window !== 'undefined' && (window.visualViewport?.height ?? window.innerHeight)
      if (
        typeof currentViewportHeight === 'number' &&
        typeof viewportInitialHeightRef.current === 'number' &&
        currentViewportHeight < viewportInitialHeightRef.current - 150
      ) {
        return
      }

      setBottomHeight(bottomRef.current.offsetHeight)
    }

    updateHeight()

    const resizeObserver = new ResizeObserver(() => updateHeight())
    if (bottomRef.current != null) {
      resizeObserver.observe(bottomRef.current)
    }

    // Also update initial viewport height if the window is resized (desktop/orientation changes)
    const onWindowResize = () => {
      if (typeof window !== 'undefined') {
        viewportInitialHeightRef.current = window.innerHeight
        updateHeight()
      }
    }

    window.addEventListener('resize', onWindowResize)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', onWindowResize)
    }
  }, [])

  const handleNext = (obj: Obj): void => {
    const mergedObj = {
      ...objs,
      ...obj
    }
    setObj(mergedObj)

    if (shouldSkipVerification && currentStepId === 'sim') {
      autoSkipHandledRef.current = true
      const contactIndex = stepIds.indexOf('contact')
      if (contactIndex >= 0) {
        blurActiveEditableElement()
        setCurrentStep(contactIndex)
        scrollToSection('contact')
        return
      }
    }

    const lastStepIndex = stepIds.length - 1
    if (currentStep === lastStepIndex) {
      const durationSeconds = Math.max(
        1,
        Math.round((Date.now() - subStepStartedAtRef.current) / 1000)
      )
      isCompletedRef.current = true
      trackNewNumberEvent('time_spent_step', {
        step_name: 'registration_info',
        sub_step_name: stepIds[currentStep] ?? 'unknown',
        time_spent_seconds: durationSeconds
      })
      trackNewNumberEvent('step_complete', {
        step_name: 'registration_info'
      })
      onClick(mergedObj ?? {})
    } else {
      setCurrentStep(currentStep + 1)
    }
  }
  const handleBack = (): void => {
    setCurrentStep(currentStep - 1)
  }

  const handleVerificationComplete = (payload: {
    instanceId: string
    regno: string
  }): void => {
    const mergedObj = {
      ...objs,
      instanceId: payload.instanceId
    }

    setObj(mergedObj)
    setGuardianRegno(payload.regno)

    if (shouldSkipVerification) {
      autoSkipHandledRef.current = true
      const contactIndex = stepIds.indexOf('contact')
      if (contactIndex >= 0) {
        blurActiveEditableElement()
        setCurrentStep(contactIndex)
        scrollToSection('contact')
        return
      }
    }

    if (isPrepaidStars) {
      blurActiveEditableElement()
      setCurrentStep((prev) => prev + 1)
      return
    }

    isCompletedRef.current = true
    trackNewNumberEvent('step_complete', {
      step_name: 'registration_info'
    })
    onClick(mergedObj)
  }

  const handleChangeStep = (step: number): void => {
    setCurrentStep(step)

    if (step === 1) {
      setLiveSelectedServices({})
      setObj((prev) => ({
        ...prev,
        choosenPackage: '',
        choosenInfo: {
          choosenPackage: undefined,
          choosenCallMM: undefined,
          choosenDataMM: undefined,
          choosenSmsMM: undefined,
          choosenVideoStreamingMM: undefined,
          choosenEntertainmentMM: undefined
        }
      }))
    }
  }

  const handleContactContinue = (): void => {
    if (Date.now() - contactTouchSubmitAtRef.current < 400) {
      return
    }

    if (isContactSubmitting) {
      return
    }

    if (contactSubmitHandlerRef.current != null) {
      contactSubmitHandlerRef.current()
      return
    }

    if (typeof document !== 'undefined') {
      const hiddenButton = document.querySelector('#contact-continue')
      if (hiddenButton instanceof HTMLElement) {
        hiddenButton.click()
      }
    }
  }

  const handleContactContinueTouchEnd = (): void => {
    if (!isContactValid || isContactSubmitting) {
      return
    }

    contactTouchSubmitAtRef.current = Date.now()

    if (contactSubmitHandlerRef.current != null) {
      contactSubmitHandlerRef.current()
      return
    }

    if (typeof document !== 'undefined') {
      const hiddenButton = document.querySelector('#contact-continue')
      if (hiddenButton instanceof HTMLElement) {
        hiddenButton.click()
      }
    }
  }

  const selectedServices = React.useMemo(() => {
    if (!liveSelectedServices) return []

    const list: serviceDetail[] = []

    if (liveSelectedServices?.choosenPackage != null) {
      const pkg = liveSelectedServices.choosenPackage
      if (pkg.data != null) list.push(pkg.data)
      if (pkg.call != null) list.push(pkg.call)
    }

    const manualServices: Array<serviceDetail | undefined> = [
      liveSelectedServices?.choosenDataMM,
      liveSelectedServices?.choosenCallMM,
      ((liveSelectedServices?.choosenSmsMM) != null)
        ? { ...liveSelectedServices.choosenSmsMM, isadditionalService: true }
        : undefined,
      ((liveSelectedServices?.choosenVideoStreamingMM) != null)
        ? { ...liveSelectedServices.choosenVideoStreamingMM, isadditionalService: true }
        : undefined,
      ...(liveSelectedServices?.choosenEntertainmentMM ?? []).map(s => ({
        ...s,
        isadditionalService: true
      }))
    ]

    const validServices = manualServices.filter(
      (s): s is serviceDetail => s != null && !!s.name && !!s.price
    )

    list.push(...validServices)

    return list
  }, [liveSelectedServices])

  const totalPrice = React.useMemo(() => {
    const chosenPackage = liveSelectedServices?.choosenPackage
    const packageServices = chosenPackage?.services ?? []

    const isCustom =
      (chosenPackage == null) || packageServices.length === 0

    const hasAddOns = selectedServices.some(
      (s) => !packageServices.some((pkgService) => pkgService.id === s.id)
    )

    if (isCustom) {
      return selectedServices.reduce((sum, s) => sum + (s.price ?? 0), 0)
    }

    if (hasAddOns) {
      const extraServices = selectedServices.filter(
        (s) => !packageServices.some((pkgService) => pkgService.id === s.id)
      )
      const extrasTotal = extraServices.reduce(
        (sum, s) => sum + (s.price ?? 0),
        0
      )
      return (typeof chosenPackage?.price === 'number' ? chosenPackage?.price : 0) + extrasTotal
    }

    return chosenPackage?.price ?? 0
  }, [selectedServices, liveSelectedServices])

  const selectedPackageName = React.useMemo(() => {
    if (objs?.choosenPackage) {
      return objs?.choosenPackage?.split(' - ')[1] ?? ''
    }
    return ''
  }, [objs?.choosenPackage])

  useEffect(() => {
    const fetchStarsSupportInfo = async (): Promise<void> => {
      const currentInstanceId = objs?.instanceId ?? ''
      if (currentInstanceId === '' || !isPrepaidStars) {
        return
      }

      try {
        let registerNumber = guardianRegno.trim()

        if (registerNumber === '') {
          if (starsSupportInstanceFetchRef.current === currentInstanceId) {
            return
          }

          starsSupportInstanceFetchRef.current = currentInstanceId
          const instance = await getInstanceV2(currentInstanceId, locale)
          registerNumber = instance?.register ?? instance?.danInfo?.regnum ?? ''
        }

        if (registerNumber !== '') {
          const requestKey = `${currentInstanceId}:${registerNumber}`
          if (starsSupportRequestKeyRef.current === requestKey) {
            return
          }

          starsSupportRequestKeyRef.current = requestKey
          setGuardianRegno(registerNumber)
          const phoneInfo = await getPhoneNumbersByRegno(registerNumber)
          const phoneNumbers = phoneInfo?.phoneNumbers ?? []
          setSuggestedPhoneNumbers(phoneNumbers)
          const firstPhone = phoneNumbers[0] ?? ''
          setSuggestedPhoneNumber(firstPhone)
          if (phoneNumbers.length === 0) {
            starsSupportRequestKeyRef.current = ''
          }
        }
      } catch (error) {
        starsSupportRequestKeyRef.current = ''
        starsSupportInstanceFetchRef.current = ''
        console.error(error)
      }
    }

    void fetchStarsSupportInfo()
  }, [
    getInstanceV2,
    getPhoneNumbersByRegno,
    guardianRegno,
    isPrepaidStars,
    locale,
    objs?.instanceId,
    stepIds
  ])

  const handleCheck = async (): Promise<void> => {
    if (state.checkBtnDisabled === true) return
    try {
      setState({ checkBtnDisabled: true })
      const res = await numberv2.checkReferral(
        state.inputRefValue !== undefined ? state.inputRefValue : ''
      )
      if (res?.isValid !== undefined && res?.isValid) {
        if (
          typeof window !== 'undefined' &&
          state.inputRefValue !== 'undefined'
        ) {
          sessionStorage.setItem('mc_refCode', state.inputRefValue ?? '')
        }
        setState({ isRefCodeValid: true })
      } else {
        showError(translate('referralCodeNotValid'))
        setState({ checkBtnDisabled: false })
      }
    } catch (e) {
      showError(translate('error'))
      console.error(e)
    }
  }

  const ConfirmedEmail: FunctionComponent = () => {
    return (
      <div className='bg-surfaceInverse rounded-xl p-4'>
        <TextField
          label={translate('email')}
          variant='outlined'
          className='w-full rounded-lg'
          value={objs?.email}
          disabled
          sx={{
            '& fieldset': {
              borderRadius: '12px',
              borderColor: '#D6D6D6'
            },
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: '#D6D6D6'
              }
            },
            '& .MuiOutlinedInput-input.Mui-disabled': {
              color: '#6E7882',
              WebkitTextFillColor: '#6E7882'
            },
            '& label.Mui-disabled': {
              color: '#6E7882'
            },
            '& label.Mui-focused': {
              color: '#262626'
            },
            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
            {
              display: 'none'
            },
            '& input[type=number]': {
              MozAppearance: 'textfield'
            }
          }}
        />
      </div>
    )
  }

  interface PrevStepProps {
    title: string
    value: string
    index: number
  }
  const prevStep = (props: PrevStepProps): JSX.Element => {
    return (
      <div className='flex justify-between bg-surfaceInverse rounded-xl p-4 '>
        <div className='flex flex-col gap-2'>
          <span className='text-[#6E7882] text-xs'>{props.title}</span>
          <span className='text-[#6E7882] text-sm font-bold truncate max-w-[250px]'>
            {props.value ?? ''}
          </span>
        </div>
        <DarkButton
          type='outlined'
          size='sm'
          onClick={() => handleChangeStep(props.index)}
        >
          {translate('change')}
        </DarkButton>
      </div>
    )
  }
  const steps = [
    {
      label: translate('email.registration'),
      body: <EmailInput next={handleNext} back={handleBack} />,
      past: <ConfirmedEmail />,
      id: 'email'
    },
    {
      label: translate('packageselection'),
      body: (
        <ChoosePackage
          next={handleNext}
          back={handleBack}
          instanceId={objs?.instanceId ?? ''}
          referralCode={state.inputRefValue}
          onSelectionChange={setLiveSelectedServices}
        />
      ),
      past: prevStep({
        title: translate('data'),
        value: objs?.choosenPackage ?? '',
        index: 1
      }),
      id: 'package'
    },
    {
      label: translate('number.choose'),
      body: (
        <ChooseNumber
          next={handleNext}
          instanceId={objs?.instanceId ?? ''}
          rateplan={objs?.rateplan ?? ''}
        />
      ),
      past: prevStep({
        title: translate('number.chosen'),
        value: objs?.choosenNumber ?? '',
        index: 2
      }),
      id: 'number'
    },
    {
      label: translate('sim.choose'),
      body: (
        <ChooseSim
          next={handleNext}
          back={handleBack}
          instanceId={objs?.instanceId ?? ''}
        />
      ),
      past: prevStep({
        title: translate('sim.choosen'),
        value: objs?.choosenSim ?? '',
        index: 3
      }),
      id: 'sim'
    },
    ...(isPrepaidStars
      ? [{
          label: translate('verify.confirmation'),
          body: (
            <VerificationSection
              instanceId={objs?.instanceId ?? ''}
              initialRegno={guardianRegno}
              onVerified={handleVerificationComplete}
            />
          ),
          past: <div />,
          id: 'verification'
        },
        ]
      : []),
    {
      label: translate('stars.contact'),
      body: (
        <ContactInfo
          next={handleNext}
          back={handleBack}
          onValidationChange={(valid) => setContactValid(valid)}
          onSubmittingChange={(submitting) => setContactSubmitting(submitting)}
          onSubmitReady={(submitHandler) => {
            contactSubmitHandlerRef.current = submitHandler
          }}
          instanceId={objs?.instanceId ?? ''}
          initialPhoneNumber={isPrepaidStars ? suggestedPhoneNumber : undefined}
          initialPhoneNumbers={isPrepaidStars ? suggestedPhoneNumbers : undefined}
          initialGuardianRegno={isPrepaidStars ? guardianRegno : undefined}
          // pass initial values from query (if present) to allow webview/native to prefill
          initialSelectedChildRegno={initialSelectedChildRegnoFromQuery}
          initialContact1={initialContact1FromQuery}
          isPrepaidStars={isPrepaidStars}
        />
      ),
      past: <div />,
      id: 'contact'
    }
  ]

  useEffect(() => {
    const previousIndex = previousSubStepIndexRef.current

    if (previousIndex !== null && previousIndex !== currentStep) {
      const durationSeconds = Math.max(
        1,
        Math.round((Date.now() - subStepStartedAtRef.current) / 1000)
      )

      trackNewNumberEvent('time_spent_step', {
        step_name: 'registration_info',
        sub_step_name: stepIds[previousIndex] ?? 'unknown',
        time_spent_seconds: durationSeconds
      })
    }

    currentStepRef.current = currentStep
    previousSubStepIndexRef.current = currentStep
    subStepStartedAtRef.current = Date.now()

    trackNewNumberEvent('page_view_step', {
      step_name: 'registration_info',
      sub_step_name: stepIds[currentStep] ?? 'unknown'
    })
  }, [currentStep, isPrepaidStars, stepIds])

  useEffect(() => {
    return () => {
      const activeSubStep = stepIds[currentStepRef.current] ?? 'unknown'
      const durationSeconds = Math.max(
        1,
        Math.round((Date.now() - subStepStartedAtRef.current) / 1000)
      )

      trackNewNumberEvent('time_spent_step', {
        step_name: 'registration_info',
        sub_step_name: activeSubStep,
        time_spent_seconds: durationSeconds
      })

      if (!isCompletedRef.current) {
        trackNewNumberEvent('abandon_step', {
          step_name: 'registration_info',
          sub_step_name: activeSubStep
        })
      }
    }
  }, [isPrepaidStars, stepIds])

  useEffect(() => {
    if (!isWebView) return
    if (currentStepId !== 'contact') return

    blurActiveEditableElement()
    scrollToSection('contact')
  }, [currentStepId, isWebView, scrollToSection])

  return (
    <div
      className='w-full xl:w-1/2 lg:w-3/4 md:w-4/5 h-full flex flex-col justify-center p-3 md:p-0'
      style={{ marginBottom: showBottomSummary ? bottomHeight : 0 }}
    >
      <div className='flex bg-surfaceMid rounded-2xl p-2 m-0'>
        <div className='flex w-full bg-surfaceInverse text-textMid border-[1px] border-white rounded-xl items-center'>
          <TextField
            label={translate('referralPromoCode')}
            className='flex-1 rounded-l-xl border-secondaryHighStroke bg-surfaceInverse text-textMid'
            value={state.inputRefValue}
            onChange={(e) => {
              const inputValue = e.target.value
              if (/^[a-zA-Z0-9]*$/.test(inputValue)) {
                if (inputValue.length === 0) {
                  setState({ checkBtnDisabled: true })
                } else {
                  setState({ checkBtnDisabled: false })
                }
                setState({ inputRefValue: inputValue })
              }
            }}
            InputProps={{
              readOnly: state.isRefCodeValid,
              inputProps: {
                maxLength: 8
              },
              style: {
                color: 'var(--color-text-textHigh)',
                '&::placeholder': {
                  color: 'var(--color-text-secondaryHighStroke)'
                }
              } as any
            }}
            InputLabelProps={{
              style: { color: 'var(--color-text-textHigh)' }
            }}
            sx={{
              '& fieldset': {
                border: 'none'
              },
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              },
              '& label.Mui-focused': {
                color: '#262626'
              },
              '& input': {
                border: 'none',
                outline: 'none',
                color: 'textHigh'
              },
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
              {
                display: 'none'
              },
              '& input[type="text"]': {
                MozAppearance: 'textfield'
              }
            }}
          />
          {state.isRefCodeValid === false
            ? (
              <DarkButton
                classNames='w-30 text-textInverse text-base font-bold m-2'
                onClick={() => {
                  void handleCheck()
                }}
                disabled={state.checkBtnDisabled}
              >
                {translate('referralCodeActivate')}
              </DarkButton>
              )
            : (
              <Button
                className='m-2'
                disabled
                sx={{
                  borderRadius: '12px',
                  padding: '0 16px',
                  textTransform: 'none'
                }}
              >
                <img
                  src='/icons/verified.svg'
                  height={20}
                  width={20}
                  alt='info'
                />
              </Button>
              )}
        </div>
      </div>
      {steps.map((step, index) => {
        const hideLockedStarsContact =
          isPrepaidStars &&
          step.id === 'contact' &&
          currentStep < index

        if (hideLockedStarsContact) {
          return null
        }

        return (
          <div
            key={index}
            id={step.id}
            className={classNames(
              'flex flex-col gap-4 my-4',
              isWebView ? '' : 'transition-all duration-500'
            )}
          >
            <span
              className={classNames(
                currentStep !== index
                  ? 'text-[#6E7882] font-bold'
                  : 'font-extrabold text-textHigh'
              )}
            >
              {index + 1}. {step.label}
            </span>
            {currentStep > index ? (
              <div>{step.past}</div>
            ) : (
              <div
                className={classNames(
                  currentStep < index ? 'opacity-50 pointer-events-none' : ''
                )}
              >
                {typeof window !== 'undefined' ? step.body : null}
              </div>
            )}
          </div>
        )
      })}
      {showBottomSummary && (
        <div
          ref={bottomRef}
          className='flex-col fixed md:sticky bottom-0 left-0 w-full bg-surfaceLow z-50 p-4 pt-0 gap-1 shadow-[0px_-2px_8px_0px_rgba(0,0,0,0.08)] rounded-t-xl'
          onTouchStartCapture={(e) => {
            if (currentStepId === 'contact') {
              blurActiveEditableElement(e.target)
            }
          }}
        >
          <div
            className='flex justify-between items-center h-12'
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <div className='flex items-center gap-2'>
              <div>
                <Image
                  width={18}
                  height={18}
                  alt='img'
                  src={
                    theme === 'dark'
                      ? isExpanded
                        ? resolveImageUrl('/uploads/icons/chevron-down-white.svg')
                        : resolveImageUrl('/uploads/icons/chevron-up-white.svg')
                      : isExpanded
                        ? resolveImageUrl('/uploads/icons/chevron-down.svg')
                        : resolveImageUrl('/uploads/icons/chevron-up.svg')
                  }
                />
              </div>
              <p className='text-secondary text-sm font-bold'>
                {translate('package.viewDetails')}
              </p>
            </div>
            <p className='text-base font-bold text-textHigh'>
              {moneyFormatter(totalPrice)}
            </p>
          </div>
          {isExpanded && (
            <div className='py-2 space-y-1 text-sm text-gray-600'>
              {selectedServices.map((item, index) => (
                <div key={index} className='flex items-center justify-between'>
                  <div className='flex gap-1 max-w-[65%] truncate'>
                    <p className='text-sm text-textMid font-medium gap-1'>
                      {item.name}
                    </p>
                    {(
                      (item.name?.toLowerCase().includes('сүлжээнд') ||
                    item.name?.toLowerCase().includes('net')) &&
                    !(item.isadditionalService ?? false)
                    ) && (
                      <span className='text-textMid'>
                        {translate('mymobi.unlimited')}
                      </span>
                    )}
                    {item.name?.toLowerCase().includes('gb') && (
                      <span className='text-textMid'>
                        {translate('mymobi.data')}
                      </span>
                    )}
                  </div>
                  <p className='text-sm text-textMid font-medium'>
                    {moneyFormatter(item.price)}
                  </p>
                </div>
              ))}
            </div>
          )}
          {currentStepId === 'contact' && (
            <div onTouchEndCapture={handleContactContinueTouchEnd}>
              <DarkButton
                fullwidth
                loading={isContactSubmitting}
                disabled={!isContactValid || isContactSubmitting}
                onClick={handleContactContinue}
              >
                {translate('continue')}
              </DarkButton>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default Step1
